import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import get from "lodash/get";
import Typography from "@material-ui/core/Typography";
import getImageUrl from "@configuratorware/configurator-frontendgui/App/Utils/getImageUrl";
import customClassName from "@configuratorware/configurator-frontendgui/App/Utils/customClassName";
import { Hyphenated } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import { withStyles } from "@material-ui/core/styles";

const ProductPart = ({
  displaySwitchedOptions,
  onSelect,
  optionclassification,
  selectedOption,
  verticalMode,
  setAnchorRef,
  classes,
  optionListVisible,
}) => {
  const selected =
    optionListVisible &&
    get(selectedOption, "identifier", null) ===
      get(optionclassification, "identifier", "");
  const direction = verticalMode ? "vertical" : "horizontal";

  const displayImage = () => {
    const imageUrl =
      displaySwitchedOptions && selectedOption
        ? getImageUrl(selectedOption.thumbnail)
        : getImageUrl(optionclassification.thumbnail);

    return (
      <i
        className={`${classes.icon} ${direction}`}
        style={imageUrl ? { backgroundImage: "url(" + imageUrl + ")" } : {}}
      />
    );
  };

  const displayText = () => {
    return (
      <Typography
        className={classes.rootTypography}
        variant="body1"
        component="span"
      >
        <Hyphenated>{optionclassification.title}</Hyphenated>
      </Typography>
    );
  };

  const onClick = (event) => {
    event.stopPropagation();
    onSelect(optionclassification);
  };

  return (
    <div
      className={clsx(classes.root, direction, customClassName("component"))}
      ref={setAnchorRef}
    >
      <div
        className={`${classes.marker} ${direction} ${
          selected ? "selected" : ""
        }`}
      />
      <div className={`${classes.content} ${direction}`} onClick={onClick}>
        {displayImage()}
        {displayText()}
      </div>
    </div>
  );
};

const MARKER_POSITION = {
  left: "left",
  right: "right",
  top: "top",
};

ProductPart.defaultProps = {
  optionclassification: {},
  onSelect: () => {},
  verticalMode: true,
};

ProductPart.propTypes = {
  displaySwitchedOptions: PropTypes.bool,
  onSelect: PropTypes.func,
  optionclassification: PropTypes.object,
  selectedOption: PropTypes.object,
  verticalMode: PropTypes.bool,
  setAnchorRef: PropTypes.func,
  classes: PropTypes.object,
  optionListVisible: PropTypes.bool,
};

export const StyledProductPart = withStyles(
  (theme) => {
    const captionMaxHeight = `calc(${theme.creator.productPart.captionPaddingTop} + ${theme.creator.productPart.lineHeight} * 2)`;
    const iconMaxHeigth = `calc(${theme.creator.productPart.itemSize} - ${captionMaxHeight}  - ${theme.creator.productPart.captionPaddingTop})`;

    const iconSizeWithoutMarker = `calc(${theme.creator.productPart.itemSize})`;

    return {
      root: {
        [theme.breakpoints.up("sm")]: {
          width: theme.creator.productPart.selectedItemSize,
        },
        width: theme.creator.productPart.itemSize,
        height: "auto",

        "&.horizontal": {
          padding: "0 10px",
        },
        "&.vertical": {
          marginLeft: "auto",
          display: "flex",
          flexDirection: "row-reverse",
        },
        tapHighlightColor: theme.tapHighlightColor,
      },
      content: {
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        textAlign: "center",
        position: "relative",

        "&.horizontal": {
          width: theme.creator.productPart.itemSize,
          height: "auto",
          float: MARKER_POSITION.top,
          whiteSpace: "normal",
          "& span": {
            marginBottom: 0,
          },
        },
        "&.vertical": {
          width: "100%",
          maxHeight: 120,
          float: theme.creator.productPartsList.markerVerticalPosition,
          margin: 0,
        },
      },
      icon: {
        maxHeight: iconMaxHeigth,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        display: "block",
        "&.horizontal": {
          width: theme.creator.productPart.itemSize,
          height: `calc(0.75 * ${theme.creator.productPart.itemSize})`,
          [theme.breakpoints.down("xs")]: {
            maxHeight: `calc(0.75 * ${iconMaxHeigth})`,
          },
        },
        "&.vertical": {
          width: iconSizeWithoutMarker,
          height: "calc(0.75*" + iconSizeWithoutMarker + ")",
        },
      },
      marker: {
        backgroundColor: "transparent",
        "&.horizontal": {
          width: theme.creator.productPart.itemSize,
          height: theme.creator.productPart.markerThickness,
        },
        "&.vertical": {
          width: theme.creator.productPart.markerThickness,
          margin: theme.spacing(0, 0, 1, 0),
        },
        "&.selected": {
          [theme.breakpoints.down("sm")]: {
            marginBottom: 4,
          },
          [theme.breakpoints.down("xs")]: {
            marginBottom: 0,
          },
          backgroundColor: theme.palette.primary.main,
        },
      },
      rootTypography: {
        padding: "3px 0 2px 7px",
        marginBottom: 5,
        display: "-webkit-box",
        overflow: "hidden",
        wordWrap: "break-word",
        maxWidth: theme.creator.productPart.itemSize,
        lineHeight: 1,
        boxOrient: "vertical",
        lineClamp: 3,
        minHeight: 20,
      },
    };
  },
  { name: "ProductPart" }
)(ProductPart);

export default StyledProductPart;
